import { OptionItemState } from '@deltek/specification-client/core/Models/textContentElement'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { PROPERTY_EDITOR_PILL_STATE_VARIANT as PillStateVariants } from '../constants'
import { PillSelectionState, SetPillSelectionProps } from '../types'

/** Valid pill types for selection */
const VALID_PILL_TYPES = [
    NODE_TYPES.SelectionOption,
    NODE_TYPES.OptionGroup,
    NODE_TYPES.ReplacementOption,
] as const

/** Default state for pill selection functionality */
const defaultPillSelectionState = {
    selectedPill: null,
    parentItem: null,
    isSelected: false,
    multiType: '',
    contentItemViewNodes: [],
}

/**
 * Validates the pill selection props
 */
const validatePillSelectionProps = (props: SetPillSelectionProps): boolean => {
    if (!props) {
        console.warn('Invalid pill selection props: props is required')
        return false
    }

    const { selectedPill, parentItem, contentItemViewNodes } = props

    if (!selectedPill && !parentItem) {
        console.warn(
            'Invalid pill selection props: selectedPill or parentItem is required'
        )
        return false
    }

    if (!Array.isArray(contentItemViewNodes)) {
        console.warn(
            'Invalid pill selection props: contentItemViewNodes must be an array'
        )
        return false
    }

    return true
}

/**
 * Calculates the new state for a pill based on current state and selection status
 * 
 * State transitions:
 * - OptedOut: Permanently opted out, remains OptedOut
 * - Not selected -> Selected: Becomes OptedIn
 * - Selected -> Deselected: Becomes InActive (can be re-enabled)
 */
const calculateNewPillState = (
    multiType: string,
    isSelected: boolean,
    isValid: boolean
): OptionItemState => {
    // Validate multiType if provided
    const validStates = Object.values(PillStateVariants) as string[]
    if (multiType && !validStates.includes(multiType)) {
        console.warn(`Invalid multiType provided: ${multiType}`)
    }

    // Handle OptedOut state - always remains OptedOut
    if (multiType === PillStateVariants.OptedOut) {
        return PillStateVariants.OptedOut
    }

    // When item is NOT currently selected, selecting it opts it in
    if (!isSelected) {
        return PillStateVariants.OptedIn
    }

    // When item IS currently selected, deselecting it makes it inactive
    // InActive means temporarily disabled but can be re-enabled
    return PillStateVariants.InActive
}

/**
 * Checks if a pill type is valid for selection
 */
const isValidPillType = (type: string): boolean => {
    // Type-safe check without using 'as any'
    return VALID_PILL_TYPES.some(validType => validType === type)
}

export const usePillSelectionStore = create<PillSelectionState>((set, get) => ({
    ...defaultPillSelectionState,

    // Set the current pill selection
    setPillSelection: (props: SetPillSelectionProps) => {
        if (!validatePillSelectionProps(props)) {
            console.error('Failed to set pill selection: invalid props')
            return
        }

        const {
            selectedPill,
            parentItem,
            isSelected = false,
            contentItemViewNodes = [],
            multiType = '',
        } = props

        try {
            set({
                selectedPill,
                parentItem,
                isSelected,
                multiType,
                contentItemViewNodes,
            })
        } catch (error) {
            console.error('Error setting pill selection:', error)
        }
    },

    // Reset the pill selection state
    resetPillSelectionState: () => {
        try {
            set(defaultPillSelectionState)
        } catch (error) {
            console.error('Error resetting pill selection state:', error)
        }
    },

    // Check if a pill variant is valid for selection
    isValidVariant: (type: string): boolean => {
        if (!type || typeof type !== 'string') {
            console.warn('Invalid type provided to isValidVariant')
            return false
        }
        return isValidPillType(type)
    },

    // Calculate the new state for a pill based on selection status
    calculatePillStateChange: (
        multiType: string,
        isSelected: boolean,
        isValid: boolean
    ): OptionItemState => {
        try {
            return calculateNewPillState(multiType, isSelected, isValid)
        } catch (error) {
            console.error('Error calculating pill state change:', error)
            // Return InActive as default to allow recovery through user action
            // Using InActive instead of OptedOut allows the user to re-enable the item
            return PillStateVariants.InActive
        }
    },

    // Get current selection state (helper method)
    getCurrentSelection: () => {
        const state = get()
        return {
            selectedPill: state.selectedPill,
            parentItem: state.parentItem,
            isSelected: state.isSelected,
            multiType: state.multiType,
        }
    },

    // Check if a specific pill is currently selected
    isPillSelected: (pillId: string): boolean => {
        if (!pillId) return false
        const state = get()
        return state.selectedPill?.id === pillId
    },

    // Clear selection for a specific pill
    clearPillSelection: (pillId?: string) => {
        const state = get()

        if (pillId && state.selectedPill?.id !== pillId) {
            return // Not the selected pill, no action needed
        }

        set({
            ...state,
            selectedPill: null,
            isSelected: false,
        })
    },
}))

/**
 * A custom hook that retrieves a shallowly selected value from the `usePillSelectionStore` store.
 *
 * Sample usage:
 * ```typescript
 * const { selectedPill, setPillSelection } = usePillSelectionSelector((state) => ({
 *   selectedPill: state.selectedPill,
 *   setPillSelection: state.setPillSelection,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const usePillSelectionSelector = <T>(
    selector: (state: PillSelectionState) => T
): T => {
    try {
        return usePillSelectionStore(useShallow(selector))
    } catch (error) {
        console.error('Error in usePillSelectionSelector:', error)
        throw error
    }
}

/**
 * Convenience hook for common pill selection operations
 */
export const usePillSelection = () => {
    return usePillSelectionSelector((state) => ({
        selectedPill: state.selectedPill,
        parentItem: state.parentItem,
        isSelected: state.isSelected,
        multiType: state.multiType,
        contentItemViewNodes: state.contentItemViewNodes,
        setPillSelection: state.setPillSelection,
        resetPillSelectionState: state.resetPillSelectionState,
        isValidVariant: state.isValidVariant,
        calculatePillStateChange: state.calculatePillStateChange,
        getCurrentSelection: state.getCurrentSelection,
        isPillSelected: state.isPillSelected,
        clearPillSelection: state.clearPillSelection,
    }))
}

// Export utility functions separately (not as part of the store)
export { updateElementById, updateElements } from '../utils/pillStateHelpers'
